import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import InitialsAvatar from '../../components/InitialsAvatar';
import { useAsync } from '../../lib/useAsync';
import { membersApi, mediaApi } from '../../api/resources';
import { PageLoader, ErrorState, EmptyState, Field } from '../../components/ui';
import { useToast } from '../../context/ToastContext';

const EMPTY_FORM = {
  id: null,
  first_name: '',
  last_name: '',
  club_name: '',
  email: '',
  phone: '',
  position: '',
  is_leadership: false,
  bio: '',
  joined_on: '',
};

// Photo state: `changed` gates whether `photo_media_id` is sent at all.
//   changed=false            -> omit photo_media_id (photo unchanged)
//   changed=true, mediaId=n  -> attach MediaFile n
//   changed=true, mediaId=null -> remove photo (photo_media_id: null)
const NO_PHOTO_CHANGE = { changed: false, mediaId: null, preview: null };

// Client-side hint only. The server is the authority on username validity and
// uniqueness; its 422 copy is shown verbatim.
const USERNAME_HINT =
  '3 to 30 characters. Letters, numbers, dot, underscore and hyphen only. Not case sensitive.';

function fullName(form) {
  return `${form.first_name} ${form.last_name}`.trim() || 'New member';
}

export default function AdminMembers() {
  const toast = useToast();
  const { data, loading, error, reload } = useAsync(() => membersApi.list({ per_page: 100 }), []);

  const [form, setForm] = useState(null); // null = form closed
  const [photo, setPhoto] = useState(NO_PHOTO_CHANGE);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmReset, setConfirmReset] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [confirmLogin, setConfirmLogin] = useState(null);
  const [creatingLogin, setCreatingLogin] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [loginError, setLoginError] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [renameError, setRenameError] = useState('');
  const [renaming, setRenaming] = useState(false);

  if (loading) return <PageLoader />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const members = data?.items || [];

  function openCreate() {
    setErrors({});
    setPhoto(NO_PHOTO_CHANGE);
    setForm({ ...EMPTY_FORM });
  }

  function openEdit(m) {
    setErrors({});
    setPhoto({ changed: false, mediaId: null, preview: m.photo_url || m.photo_path || null });
    setForm({
      id: m.id,
      first_name: m.first_name || '',
      last_name: m.last_name || '',
      club_name: m.club_name || '',
      email: m.email || '',
      phone: m.phone || '',
      position: m.position || '',
      is_leadership: Boolean(m.is_leadership),
      bio: m.bio || '',
      joined_on: m.joined_on || '',
    });
  }

  function closeForm() {
    setForm(null);
    setPhoto(NO_PHOTO_CHANGE);
    setErrors({});
  }

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleUpload(e) {
    const file = (e.target.files || [])[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'image'); // member photos are images only
      fd.append('alt_text', fullName(form));
      const media = await mediaApi.upload(fd);
      if (!media?.id) throw new Error('Upload succeeded but no media id was returned.');
      // Read the previewable URL from `media.url` (never `media.path`).
      setPhoto({ changed: true, mediaId: media.id, preview: media.url });
      toast.success('Photo uploaded. Save the member to apply it.');
    } catch (err) {
      toast.error(err.message || 'Photo upload failed.');
    } finally {
      setUploading(false);
    }
  }

  function removePhoto() {
    // Explicit removal -> photo_media_id: null on save.
    setPhoto({ changed: true, mediaId: null, preview: null });
  }

  function buildPayload() {
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      club_name: form.club_name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      position: form.position.trim() || null,
      is_leadership: form.is_leadership,
      bio: form.bio.trim() || null,
      joined_on: form.joined_on || null,
    };
    // Only send photo_media_id when the photo actually changed.
    if (photo.changed) payload.photo_media_id = photo.mediaId; // number or null
    return payload;
  }

  async function handleSave(e) {
    e.preventDefault();
    setErrors({});
    setBusy(true);
    try {
      const payload = buildPayload();
      if (form.id) {
        await membersApi.update(form.id, payload);
        toast.success('Member updated.');
      } else {
        await membersApi.create(payload);
        toast.success('Member added.');
      }
      closeForm();
      reload();
    } catch (err) {
      if (err?.errors && Object.keys(err.errors).length) {
        setErrors(err.errors);
        toast.error('Please fix the highlighted fields.');
      } else {
        toast.error(err?.message || 'Could not save member.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(m) {
    setBusy(true);
    try {
      await membersApi.remove(m.id);
      toast.success('Member removed.');
      setConfirmDelete(null);
      if (form?.id === m.id) closeForm();
      reload();
    } catch (err) {
      toast.error(err?.message || 'Could not remove member.');
    } finally {
      setBusy(false);
    }
  }

  async function handleReset(m) {
    setResetting(true);
    try {
      // Omit the password so the backend applies its default (Welcome123).
      await membersApi.resetPassword(m.id);
      toast.success('Password reset to Welcome123.');
      setConfirmReset(null);
    } catch (err) {
      // 422 = the member has no linked login account; surface the backend message.
      toast.error(err?.message || 'Could not reset password.');
      setConfirmReset(null);
    } finally {
      setResetting(false);
    }
  }

  function openCreateLogin(m) {
    setLoginUsername('');
    setLoginError('');
    setConfirmLogin(m);
  }

  function closeCreateLogin() {
    if (creatingLogin) return;
    setConfirmLogin(null);
    setLoginUsername('');
    setLoginError('');
  }

  async function handleCreateLogin(e) {
    e.preventDefault();
    const m = confirmLogin;
    if (!m) return;
    setLoginError('');
    setCreatingLogin(true);
    try {
      // The admin picks the username; the member's email is optional.
      const res = await membersApi.createLogin(m.id, loginUsername.trim());
      // Show the server copy verbatim (it states the temporary password, or that
      // an existing account was adopted with its password unchanged).
      toast.success(res?.message || 'Login account created.');
      setConfirmLogin(null);
      setLoginUsername('');
      reload(); // badge flips to "Has login" and shows the new username
    } catch (err) {
      // 422 messages are user-facing copy from the backend — never rewrite them.
      const message =
        err?.message || err?.errors?.username?.[0] || 'Could not create the login account.';
      setLoginError(message);
      toast.error(message);
    } finally {
      setCreatingLogin(false);
    }
  }

  function openRename(m) {
    setNewUsername('');
    setRenameError('');
    setRenameTarget(m);
  }

  function closeRename() {
    if (renaming) return;
    setRenameTarget(null);
    setNewUsername('');
    setRenameError('');
  }

  async function handleRename(e) {
    e.preventDefault();
    const m = renameTarget;
    if (!m) return;
    setRenameError('');
    setRenaming(true);
    try {
      const res = await membersApi.setUsername(m.id, newUsername.trim());
      toast.success(res?.message || 'Username updated.');
      setRenameTarget(null);
      setNewUsername('');
      reload(); // row shows the new username
    } catch (err) {
      const message = err?.message || err?.errors?.username?.[0] || 'Could not change the username.';
      setRenameError(message);
      toast.error(message);
    } finally {
      setRenaming(false);
    }
  }

  const fieldErr = (name) => errors?.[name]?.[0];

  return (
    <div className="space-y-6">
      <Seo title="Members" />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Members</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Manage the member &amp; board directory, including public board photos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/members/board-order" className="btn-outline">
            Board order
          </Link>
          <button className="btn-primary" onClick={openCreate}>
            + Add Member
          </button>
        </div>
      </div>

      {members.length === 0 ? (
        <EmptyState
          title="No members yet"
          message="Add your first member to build the directory."
          action={
            <button className="btn-primary" onClick={openCreate}>
              + Add Member
            </button>
          }
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-cream-300 bg-surface-alt text-xs uppercase tracking-wider text-ink-soft">
              <tr>
                <th className="px-5 py-3">Member</th>
                <th className="px-5 py-3">Club</th>
                <th className="px-5 py-3">Position</th>
                <th className="px-5 py-3">Leadership</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-300">
              {members.map((m) => (
                <tr key={m.id} className="hover:bg-surface-alt">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full ring-1 ring-ink/[0.06]">
                        <InitialsAvatar
                          name={m.full_name}
                          photoPath={m.photo_url || m.photo_path}
                          size="sm"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-ink">{m.full_name}</div>
                        {m.email && (
                          <div className="truncate text-xs text-ink-muted">{m.email}</div>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          {m.user_id ? (
                            <>
                              <span className="pill bg-emerald-100 text-emerald-700">Has login</span>
                              {m.username ? (
                                <span
                                  className="truncate font-mono text-xs text-ink-muted"
                                  title={`Username: ${m.username}`}
                                >
                                  @{m.username}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <span className="pill bg-surface-gray text-ink-muted">No account</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-ink-muted">{m.club_name || '—'}</td>
                  <td className="px-5 py-3 text-ink-muted">{m.position || '—'}</td>
                  <td className="px-5 py-3">
                    {m.is_leadership ? <span className="pill-gold">Leadership</span> : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        onClick={() => openEdit(m)}
                        className="btn-outline px-3 py-1.5 text-xs"
                      >
                        Edit
                      </button>
                      {/* Account actions key off the admin-only `user_id`: linked
                          account -> reset password + rename, no account ->
                          provision one (an email is not required). */}
                      {m.user_id ? (
                        <>
                          <button
                            onClick={() => setConfirmReset(m)}
                            className="btn-ghost px-3 py-1.5 text-xs"
                            disabled={busy || resetting}
                          >
                            Reset password
                          </button>
                          <button
                            onClick={() => openRename(m)}
                            className="btn-ghost px-3 py-1.5 text-xs"
                            disabled={busy || renaming}
                            title={`Change the username for ${m.full_name}`}
                          >
                            Change username
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => openCreateLogin(m)}
                          className="btn-ghost px-3 py-1.5 text-xs"
                          disabled={busy || creatingLogin}
                          title={`Create a login for ${m.full_name}`}
                        >
                          Create login
                        </button>
                      )}
                      <button
                        onClick={() => setConfirmDelete(m)}
                        className="btn-ghost px-3 py-1.5 text-xs text-red-600"
                        disabled={busy}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / edit modal */}
      {form && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-maroon-950/60 p-4 animate-fade-in-slow"
          role="dialog"
          aria-modal="true"
          aria-label={form.id ? 'Edit member' : 'Add member'}
        >
          <form
            onSubmit={handleSave}
            className="my-8 w-full max-w-2xl animate-scale-in rounded-2xl bg-surface-card shadow-lift"
          >
            <div className="flex items-center justify-between border-b border-cream-300 px-6 py-4">
              <h2 className="font-display text-lg font-semibold text-ink">
                {form.id ? 'Edit member' : 'Add member'}
              </h2>
              <button
                type="button"
                onClick={closeForm}
                className="text-ink-muted hover:text-ink"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="space-y-5 px-6 py-5">
              {/* Photo control */}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="h-28 w-28 shrink-0 overflow-hidden rounded-2xl ring-1 ring-ink/[0.06]">
                  <InitialsAvatar name={fullName(form)} photoPath={photo.preview} fill />
                </div>
                <div className="flex-1">
                  <span className="label">Profile photo</span>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="btn-outline cursor-pointer px-3 py-1.5 text-xs">
                      {photo.preview ? 'Replace photo' : 'Upload photo'}
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleUpload}
                        disabled={uploading}
                      />
                    </label>
                    {photo.preview && (
                      <button
                        type="button"
                        onClick={removePhoto}
                        className="btn-ghost px-3 py-1.5 text-xs text-red-600"
                        disabled={uploading}
                      >
                        Remove
                      </button>
                    )}
                    {uploading && <span className="text-xs text-ink-muted">Uploading…</span>}
                  </div>
                  <p className="mt-2 text-xs text-ink-muted">
                    Use a square or portrait image (at least 400×400px). It is cropped to a
                    circle on the public board.
                  </p>
                  {fieldErr('photo_media_id') && (
                    <p className="mt-1 text-xs text-red-600">{fieldErr('photo_media_id')}</p>
                  )}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="First name *" error={fieldErr('first_name')}>
                  <input
                    className="input"
                    value={form.first_name}
                    onChange={(e) => update('first_name', e.target.value)}
                    required
                  />
                </Field>
                <Field label="Last name *" error={fieldErr('last_name')}>
                  <input
                    className="input"
                    value={form.last_name}
                    onChange={(e) => update('last_name', e.target.value)}
                    required
                  />
                </Field>
              </div>

              <Field label="Club *" error={fieldErr('club_name')}>
                <input
                  className="input"
                  value={form.club_name}
                  onChange={(e) => update('club_name', e.target.value)}
                  placeholder="e.g. Zonta Club of Makati"
                  required
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Email" error={fieldErr('email')}>
                  <input
                    type="email"
                    className="input"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                  />
                </Field>
                <Field label="Phone" error={fieldErr('phone')}>
                  <input
                    className="input"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Position" error={fieldErr('position')} hint="e.g. Area Director">
                  <input
                    className="input"
                    value={form.position}
                    onChange={(e) => update('position', e.target.value)}
                  />
                </Field>
                <Field label="Joined on" error={fieldErr('joined_on')}>
                  <input
                    type="date"
                    className="input"
                    value={form.joined_on}
                    onChange={(e) => update('joined_on', e.target.value)}
                  />
                </Field>
              </div>

              {/* Leadership toggle */}
              <label className="flex cursor-pointer items-center gap-3">
                <span className="relative inline-flex">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={form.is_leadership}
                    onChange={(e) => update('is_leadership', e.target.checked)}
                  />
                  <span className="h-6 w-11 rounded-full bg-surface-gray transition-colors peer-checked:bg-maroon-700" />
                  <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                </span>
                <span className="text-sm font-medium text-ink">
                  Board / leadership member
                  <span className="block text-xs font-normal text-ink-muted">
                    Shown on the public “Our Board” page.
                  </span>
                </span>
              </label>

              <Field
                label="Bio"
                error={fieldErr('bio')}
                hint="Plain text. A short background shown on the board page."
              >
                <textarea
                  className="input"
                  rows={4}
                  value={form.bio}
                  onChange={(e) => update('bio', e.target.value)}
                />
              </Field>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-cream-300 px-6 py-4">
              <button type="button" onClick={closeForm} className="btn-ghost" disabled={busy}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={busy || uploading}>
                {busy ? 'Saving…' : form.id ? 'Save changes' : 'Add member'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-maroon-950/60 p-4"
          onClick={() => setConfirmDelete(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-surface-card p-6 shadow-lift"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-ink">Remove member?</h3>
            <p className="mt-2 text-sm text-ink-muted">
              <strong>{confirmDelete.full_name}</strong> will be removed from the directory. Their
              photo remains in the media library. This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="btn-ghost"
                disabled={busy}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="btn-danger"
                disabled={busy}
              >
                {busy ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password confirmation */}
      {confirmReset && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-maroon-950/60 p-4"
          onClick={() => !resetting && setConfirmReset(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Reset member password"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-surface-card p-6 shadow-lift"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-ink">Reset password?</h3>
            <p className="mt-2 text-sm text-ink-muted">
              Reset <strong>{confirmReset.full_name}</strong>'s password to{' '}
              <strong>Welcome123</strong>? They can set a new password after signing in. Any active
              sessions will be signed out.
            </p>
            {!confirmReset.user_id && (
              <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                This member has no linked login account. The reset will not apply until an account is
                provisioned.
              </p>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setConfirmReset(null)}
                className="btn-ghost"
                disabled={resetting}
              >
                Cancel
              </button>
              <button
                onClick={() => handleReset(confirmReset)}
                className="btn-primary"
                disabled={resetting}
              >
                {resetting ? 'Resetting…' : 'Reset password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create login */}
      {confirmLogin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-maroon-950/60 p-4"
          onClick={closeCreateLogin}
          role="dialog"
          aria-modal="true"
          aria-label="Create member login account"
        >
          <form
            onSubmit={handleCreateLogin}
            className="w-full max-w-md rounded-2xl bg-surface-card p-6 shadow-lift"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-ink">Create login account</h3>
            <p className="mt-2 text-sm text-ink-muted">
              Choose the username <strong>{confirmLogin.full_name}</strong> will sign in with. The
              temporary password will be <strong>Welcome123</strong>, which they can change after
              signing in.
            </p>
            {confirmLogin.email ? (
              <p className="mt-2 text-xs text-ink-muted">
                The account will be linked to <strong>{confirmLogin.email}</strong>.
              </p>
            ) : (
              <p className="mt-3 rounded-lg bg-surface-alt px-3 py-2 text-xs text-ink-soft">
                This member has no email address. That is fine: the account will be created without
                one. They sign in with their username, but password reset by email will not be
                available until an email is added.
              </p>
            )}

            <div className="mt-4">
              <Field label="Username *" hint={USERNAME_HINT} error={loginError || undefined}>
                <input
                  className="input"
                  value={loginUsername}
                  onChange={(e) => {
                    setLoginUsername(e.target.value);
                    if (loginError) setLoginError('');
                  }}
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  placeholder="e.g. jane.reyes"
                  required
                  autoFocus
                />
              </Field>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCreateLogin}
                className="btn-ghost"
                disabled={creatingLogin}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={creatingLogin || !loginUsername.trim()}
              >
                {creatingLogin ? 'Creating…' : 'Create login'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Change username */}
      {renameTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-maroon-950/60 p-4"
          onClick={closeRename}
          role="dialog"
          aria-modal="true"
          aria-label="Change member username"
        >
          <form
            onSubmit={handleRename}
            className="w-full max-w-md rounded-2xl bg-surface-card p-6 shadow-lift"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-ink">Change username</h3>
            <p className="mt-2 text-sm text-ink-muted">
              Update the sign-in name for <strong>{renameTarget.full_name}</strong>. Their password
              stays the same, but they must use the new username from now on.
            </p>

            <div className="mt-4 space-y-4">
              <Field label="Current username">
                <input
                  className="input bg-surface-alt"
                  value={renameTarget.username || 'Not available'}
                  readOnly
                  tabIndex={-1}
                />
              </Field>
              <Field label="New username *" hint={USERNAME_HINT} error={renameError || undefined}>
                <input
                  className="input"
                  value={newUsername}
                  onChange={(e) => {
                    setNewUsername(e.target.value);
                    if (renameError) setRenameError('');
                  }}
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  required
                  autoFocus
                />
              </Field>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={closeRename} className="btn-ghost" disabled={renaming}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={renaming || !newUsername.trim()}
              >
                {renaming ? 'Saving…' : 'Save username'}
              </button>
            </div>
          </form>
        </div>
      )}

      <p className="text-xs text-ink-muted">
        Directory wired to <code>/api/members</code> (admin CRUD). Board photos publish
        automatically and appear on the public board.
        {members.length > 0 && ` ${members.length} member(s).`}
      </p>
    </div>
  );
}
